import React, { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import './RollingGallery.css';

const RollingGallery = ({ images, pets, speed = 1, direction = 'left', height = '200px' }) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const animationRef = useRef(null);

  // Duplicate images to create a seamless loop
  const duplicateImages = [...images, ...images];

  useEffect(() => {
    const track = trackRef.current;
    let position = 0;
    let lastTimestamp = 0;
    const baseSpeed = speed;
    
    const resetTrackPosition = () => {
      if (direction === 'left') {
        // Reset position when scrolled half-way
        if (position <= -track.offsetWidth / 2) {
          position = 0;
        }
      } else {
        // For right direction
        if (position >= 0) {
          position = -track.offsetWidth / 2;
        }
      }
    };

    const animate = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = timestamp - lastTimestamp;
      lastTimestamp = timestamp;
      
      // Calculate smooth movement based on elapsed time
      const delta = baseSpeed * (elapsed / 16); // normalized to ~60fps
      
      // Move track
      if (direction === 'left') {
        position -= delta;
      } else {
        position += delta;
      }
      
      resetTrackPosition();
      track.style.transform = `translateX(${position}px)`;
      animationRef.current = requestAnimationFrame(animate);
    };

    // Start animation
    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [speed, direction, images]);

  return (
    <Box
      ref={containerRef}
      className="rolling-gallery-container"
      sx={{
        width: '100%',
        overflow: 'hidden',
        position: 'relative',
        height,
        backgroundColor: 'transparent',
      }}
    >
      <Box
        ref={trackRef}
        className="rolling-track"
        sx={{
          display: 'flex',
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
        }}
      >
        {duplicateImages.map((image, index) => (
          <Box
            key={index}
            className="rolling-item"
            sx={{
              height: '100%',
              minWidth: { xs: '200px', sm: '250px', md: '300px' },
              padding: 1,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'relative', height: '100%', width: '100%', borderRadius: 2 }}>
              <Box
                component="img"
                src={image.src || image}
                alt={image.alt || `Rolling Gallery Image ${index}`}
                className="rolling-image"
                sx={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 20px rgba(196, 100, 148, 0.4)',
                    filter: 'brightness(1.05)',
                    zIndex: 10,
                  },
                }}
              />
              
              {/* Overlay for the name */}
              <Box 
                className="patient-info-overlay"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '10px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  color: 'white',
                  borderBottomLeftRadius: 2,
                  borderBottomRightRadius: 2,
                  opacity: 0,
                  transform: 'translateY(10px)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  }
                }}
              >
                {(() => {
                  // Calculate the correct index for the pet
                  const petsIndex = index % (pets?.length || 1);
                  
                  // Get the pet if available
                  const pet = pets && pets.length > 0 ? pets[petsIndex] : null;
                  
                  return pet ? (
                    <>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 'bold', 
                          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                          fontSize: { xs: '0.9rem', sm: '1.1rem' }
                        }}
                      >
                        {pet.nombre}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                          fontSize: { xs: '0.8rem', sm: '0.9rem' } 
                        }}
                      >
                        {pet.tipo}
                      </Typography>
                    </>
                  ) : null;
                })()}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RollingGallery;
